/**
 * basket.js — the ordered list of pages an operator is building a report from.
 *
 * Page mode (ADR 0015) makes the report a list: the operator adds page types
 * one at a time, orders them, repeats one where it makes sense, and chooses a
 * page's meshes where auto-detection chose wrong. This is that list, as pure
 * functions over a plain object, so every rule below is testable without the
 * Builder and the Builder cannot bend one.
 *
 *   { template, seq, entries: [{ instanceId, typeId, picks }] }
 *
 * ── The instance id is the whole design problem ───────────────────────────
 *
 * An edit follows the page it was made on by its instance id (edits.js). So an
 * id must never be REUSED: deriving it from the list's length would hand a
 * removed page's id to the next page added, and that page would silently adopt
 * the removed page's captions and arrows — the exact reattachment editKeyFor
 * exists to prevent. Ids come from `seq`, a counter stored WITH the basket that
 * only ever goes up. Removing a page does not lower it; reordering does not
 * touch any id.
 *
 * ── Refusals are values, not exceptions ──────────────────────────────────
 *
 * Adding a page can legitimately be refused — a second Agreement, a page of
 * another template. Each operation returns `{ basket }` on success or
 * `{ basket, refused }` with the unchanged basket and a sentence the operator
 * can read, because the Builder must say why, not crash.
 */
import { RECIPES } from './draft.js'
import { formatFor } from './formats.js'
import { pageType, pageTypesFor } from './pageTypes.js'

/** An empty list for one template. A basket belongs to ONE template. */
export function newBasket(template) {
  if (!formatFor(template) && !RECIPES[template]) {
    throw new Error(`newBasket: "${template}" is not a report format`)
  }
  return { template, seq: 0, entries: [] }
}

/**
 * A list holding the template's full page order — the old whole report, as a
 * starting point to remove pages from rather than build up to.
 */
export function basketFromTemplate(template) {
  let b = newBasket(template)
  for (const t of pageTypesFor(template)) b = addPage(b, t.id).basket
  return b
}

/**
 * The template's usual pages, in their usual order, under FRESH ids.
 *
 * Numbered on from the list it replaces, never from i1. A list now starts
 * empty (ADR 0015), so i1 may be a page the operator added — a six-view page
 * captioned "Native skull" — or a page of another template. Minting the usual
 * pages as i1…iN would hand that caption to whichever usual page came first.
 * An earlier version did exactly that, on the reasoning that i1…iN always meant
 * the same pages; the empty start made that untrue.
 */
export function resetBasket(basket, template = basket?.template) {
  let b = carrySeq(newBasket(template), basket)
  for (const t of pageTypesFor(template)) b = addPage(b, t.id).basket
  return { basket: b }
}

/**
 * `fresh`, with its id counter raised to that of the list it replaces — for a
 * new list under the same case, including one for another template. Captions
 * are stored per case under the page's id, so ids are never reissued within a
 * case, whatever the template.
 */
export function carrySeq(fresh, previous) {
  return { ...fresh, seq: Math.max(fresh.seq, previous?.seq ?? 0) }
}

const refuse = (basket, refused) => ({ basket, refused })
const indexOf = (basket, instanceId) => basket.entries.findIndex((e) => e.instanceId === instanceId)

/**
 * Add a page of `typeId`, at the end or at index `at`.
 *
 * Refused when the type is not one of this basket's template — which also
 * refuses a page from another template, whose geometry this document cannot
 * lay out — and when a non-repeatable type is already present.
 */
export function addPage(basket, typeId, { at } = {}) {
  const type = pageType(basket.template, typeId)
  if (!type) {
    const label = formatFor(basket.template)?.label ?? basket.template
    return refuse(basket, `"${typeId}" is not a page of the ${label} format`)
  }
  if (!type.repeatable && basket.entries.some((e) => e.typeId === typeId)) {
    return refuse(basket, `"${type.title}" is already in this report, and it can appear only once`)
  }
  const seq = basket.seq + 1
  const entry = { instanceId: `i${seq}`, typeId, picks: {}, inputs: {} }
  const entries = [...basket.entries]
  const i = Number.isInteger(at) ? Math.max(0, Math.min(at, entries.length)) : entries.length
  entries.splice(i, 0, entry)
  return { basket: { ...basket, seq, entries }, entry }
}

/** Remove one page. `seq` stays where it is, so the id is never reissued. */
export function removePage(basket, instanceId) {
  const i = indexOf(basket, instanceId)
  if (i < 0) return refuse(basket, 'that page is no longer in this report')
  return { basket: { ...basket, entries: basket.entries.filter((_, j) => j !== i) } }
}

/** Move one page to index `to`. No id changes — edits follow the page. */
export function movePage(basket, instanceId, to) {
  const i = indexOf(basket, instanceId)
  if (i < 0) return refuse(basket, 'that page is no longer in this report')
  const entries = [...basket.entries]
  const [e] = entries.splice(i, 1)
  entries.splice(Math.max(0, Math.min(to, entries.length)), 0, e)
  return { basket: { ...basket, entries } }
}

/**
 * A second page of the same type, right after the first, with a NEW id and a
 * copy of its mesh choices and inputs — and nothing else. Its captions and
 * annotations are keyed to the original's id and stay with the original.
 */
export function duplicatePage(basket, instanceId) {
  const i = indexOf(basket, instanceId)
  if (i < 0) return refuse(basket, 'that page is no longer in this report')
  const src = basket.entries[i]
  const type = pageType(basket.template, src.typeId)
  if (!type?.repeatable) {
    return refuse(basket, `"${type?.title ?? src.typeId}" can appear only once`)
  }
  const seq = basket.seq + 1
  const entry = {
    instanceId: `i${seq}`, typeId: src.typeId,
    picks: { ...src.picks }, inputs: structuredClone(src.inputs ?? {}),
  }
  const entries = [...basket.entries]
  entries.splice(i + 1, 0, entry)
  return { basket: { ...basket, seq, entries }, entry }
}

/**
 * Set a generic page's own inputs — the meshes it shows, the view — merging
 * `patch` into what it has. Only that page changes.
 */
export function setPageInputs(basket, instanceId, patch) {
  const i = indexOf(basket, instanceId)
  if (i < 0) return refuse(basket, 'that page is no longer in this report')
  const entries = basket.entries.map((e, j) => (
    j === i ? { ...e, inputs: { ...(e.inputs ?? {}), ...patch } } : e))
  return { basket: { ...basket, entries } }
}

/**
 * Take one file off a page — and with it any slot pick that named that file.
 *
 * 🔴 The two must move together. A pick BEATS the page's file list (draft.js
 * re-admits a picked mesh to the page's library on purpose, so an operator can
 * name a mesh the page was not given). So a pick left behind after the file was
 * removed kept DRAWING it: the page row listed one file, the PDF showed
 * another, and the "built WITHOUT" note named the listed file as the one left
 * out. The only place the truth survived was a collapsed disclosure.
 *
 * Deliberately NOT a general invariant. A pick naming a mesh outside the page's
 * files is legitimate and intended; what is not legitimate is one surviving the
 * operator's explicit act of removing that file.
 */
export function removePageFile(basket, instanceId, meshId) {
  const i = indexOf(basket, instanceId)
  if (i < 0) return refuse(basket, 'that page is no longer in this report')
  const e = basket.entries[i]
  const meshes = (e.inputs?.meshes ?? []).filter((x) => x !== meshId)
  const picks = { ...(e.picks ?? {}) }
  for (const [slot, id] of Object.entries(picks)) if (id === meshId) delete picks[slot]
  const entries = basket.entries.map((x, j) => (
    j === i ? { ...x, inputs: { ...(x.inputs ?? {}), meshes }, picks } : x))
  return { basket: { ...basket, entries } }
}

/** An empty list — keeping `seq`, so no id is ever reissued. */
export function clearBasket(basket) {
  return { basket: { ...basket, entries: [] } }
}

/**
 * Choose the mesh for one slot of one page — or pass null to hand that slot
 * back to auto-detection. Only that page changes.
 */
export function setPagePick(basket, instanceId, slotKey, meshId) {
  const i = indexOf(basket, instanceId)
  if (i < 0) return refuse(basket, 'that page is no longer in this report')
  const picks = { ...basket.entries[i].picks }
  if (meshId == null) delete picks[slotKey]
  else picks[slotKey] = meshId
  const entries = basket.entries.map((e, j) => (j === i ? { ...e, picks } : e))
  return { basket: { ...basket, entries } }
}
